import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { ref, getBytes } from 'firebase/storage';
import { doc, onSnapshot } from 'firebase/firestore';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { db, storage, ensureAppCheckToken } from '../firebase/firebase';
import withProjectPermissions from '../components/withProjectPermissions';
import QuickStartGuideViewer from '../components/QuickStartGuideViewer';
import { generateSitePosterPdf } from '../utils/generateSitePosterPdf';
import { printPdfFromBlob } from '../utils/printPdf';
import {
  formatSitePosterProjectCodeLine,
  getSitePosterCodeOverlayRectPx,
  getSitePosterPreviewScale,
  SITE_POSTER_STORAGE_PATH,
} from '../utils/sitePosterConfig';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PREVIEW_WIDTH_PX = 520;

function SupportDocumentsLanding() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  /** Blob URL for react-pdf only — worker must not share ArrayBuffer with pdf-lib. */
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const templateBytesRef = useRef(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const posterActionInFlightRef = useRef(false);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    if (!projectId) return undefined;
    const unsub = onSnapshot(doc(db, 'projects', projectId), (snap) => {
      if (!snap.exists()) {
        setProject(null);
        return;
      }
      const data = snap.data();
      setProject({
        id: snap.id,
        name: data.name || 'Project',
        projectCode: data.projectCode,
        address: data.address,
        postcode: data.postcode,
      });
    });
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const appCheck = await ensureAppCheckToken(true);
        if (!appCheck.ok) {
          if (!cancelled) {
            templateBytesRef.current = null;
            setPreviewPdfUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return null;
            });
            setPdfLoadError(appCheck.errorMessage || 'App Check failed — cannot load poster.');
          }
          return;
        }
        const bytes = await getBytes(ref(storage, SITE_POSTER_STORAGE_PATH));
        if (cancelled) return;
        templateBytesRef.current = new Uint8Array(bytes);
        const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPreviewPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setPdfLoadError(null);
      } catch (e) {
        if (!cancelled) {
          templateBytesRef.current = null;
          setPreviewPdfUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
          setPdfLoadError(e?.message || 'Could not load site poster from Storage.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  const scale = getSitePosterPreviewScale(PREVIEW_WIDTH_PX);
  const overlayRect = useMemo(() => getSitePosterCodeOverlayRectPx(scale), [scale]);

  const buildPosterPdf = useCallback(async () => {
    const templateBytes = templateBytesRef.current;
    if (!templateBytes || !project?.projectCode) {
      throw new Error('Project code is required to print or download the poster.');
    }
    const outBytes = await generateSitePosterPdf(templateBytes, project.projectCode);
    return new Blob([outBytes], { type: 'application/pdf' });
  }, [project?.projectCode]);

  const runPosterAction = useCallback(
    async (action) => {
      if (posterActionInFlightRef.current) return;
      posterActionInFlightRef.current = true;
      setDownloadError('');
      try {
        await action();
      } catch (e) {
        setDownloadError(e?.message || 'Could not complete poster action.');
      } finally {
        posterActionInFlightRef.current = false;
      }
    },
    [],
  );

  const handleDownload = useCallback(() => {
    runPosterAction(async () => {
      const blob = await buildPosterPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeCode = String(project.projectCode).replace(/[^\w-]+/g, '_');
      a.href = url;
      a.download = `site-poster-${safeCode}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }, [buildPosterPdf, project?.projectCode, runPosterAction]);

  const handlePrint = useCallback(() => {
    runPosterAction(async () => {
      const blob = await buildPosterPdf();
      await printPdfFromBlob(blob);
    });
  }, [buildPosterPdf, runPosterAction]);

  if (!project) {
    return (
      <div style={{ padding: 40, color: 'var(--cc-text-muted)', backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
        Loading project…
      </div>
    );
  }

  const hasCode = Boolean(project.projectCode?.trim());

  const panelStyle = {
    background: 'var(--cc-card-bg)',
    borderRadius: 16,
    padding: 32,
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    maxWidth: 1100,
    margin: '0 auto',
    width: '90%',
  };

  return (
    <div style={{ padding: 40, backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: 8,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 500,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--cc-text-muted)';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>

      <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 8 }}>
        {project.name}
      </h1>
      {project.projectCode && (
        <p style={{ fontSize: 15, color: 'var(--cc-text-secondary)', marginBottom: 12 }}>
          <strong>Project Code:</strong> {project.projectCode}
        </p>
      )}
      <p style={{ fontSize: 16, color: 'var(--cc-text-secondary)', marginBottom: 8 }}>
        <strong>Address:</strong> {project.address || '—'}
      </p>
      <p style={{ fontSize: 16, color: 'var(--cc-text-secondary)', marginBottom: 32 }}>
        <strong>Postcode:</strong> {project.postcode || '—'}
      </p>

      <div style={panelStyle}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>
            Site poster
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!previewPdfUrl || !hasCode}
              style={supportDocActionButtonStyle(!previewPdfUrl || !hasCode, 'secondary')}
            >
              <Printer size={18} />
              Print
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!previewPdfUrl || !hasCode}
              style={supportDocActionButtonStyle(!previewPdfUrl || !hasCode, 'primary')}
            >
              <Download size={18} />
              Download
            </button>
          </div>
        </div>

        {!hasCode && (
          <p style={{ fontSize: 14, color: '#b45309', marginBottom: 16 }}>
            Add a project code in project settings before printing or downloading the poster.
          </p>
        )}

        {pdfLoadError && (
          <p style={{ fontSize: 14, color: '#b91c1c', marginBottom: 16 }}>
            Preview unavailable: {pdfLoadError} Ensure{' '}
            <code style={{ fontSize: 12 }}>{SITE_POSTER_STORAGE_PATH}</code> exists in Storage and
            rules allow read.
          </p>
        )}

        {downloadError && (
          <p style={{ fontSize: 14, color: '#b91c1c', marginBottom: 16 }}>
            {downloadError}
          </p>
        )}

        {previewPdfUrl && (
          <div
            style={{
              position: 'relative',
              width: PREVIEW_WIDTH_PX,
              maxWidth: '100%',
              margin: '0 auto',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}
          >
            <Document file={previewPdfUrl} loading={<p style={{ padding: 24 }}>Loading preview…</p>}>
              <Page
                pageNumber={1}
                width={PREVIEW_WIDTH_PX}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                renderMode="canvas"
              />
            </Document>
            {hasCode && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  left: overlayRect.left,
                  top: overlayRect.top,
                  width: overlayRect.width,
                  height: overlayRect.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <span
                  style={{
                    fontSize: `${32 * scale}px`,
                    fontWeight: 700,
                    color: '#000',
                    letterSpacing: '0.06em',
                    lineHeight: 1,
                    textAlign: 'center',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                  }}
                >
                  {formatSitePosterProjectCodeLine(project.projectCode)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ ...panelStyle, margin: '32px auto 0' }}>
        <QuickStartGuideViewer />
      </div>
    </div>
  );
}

function supportDocActionButtonStyle(disabled, variant) {
  const primary = variant === 'primary';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    border: primary ? 'none' : '1px solid var(--branding-app-primary-ring, #bfdbfe)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: disabled
      ? '#94a3b8'
      : primary
        ? '#fff'
        : 'var(--branding-app-primary, #2563eb)',
    background: disabled
      ? 'var(--branding-app-surface)'
      : primary
        ? 'var(--branding-app-primary, #2563eb)'
        : 'var(--branding-app-primary-soft, #eff6ff)',
  };
}

export default withProjectPermissions(SupportDocumentsLanding, { requireEdit: false });
